import axios from "axios";
import FileSaver from "file-saver";
import { useDrupalSettings } from "../composables/useDrupalSettings";

const { config } = useDrupalSettings();

const raftRestClient = {
  headers: {},
  call: {},
  initAxiosClient(baseUrl = "") {
    if (baseUrl) {
      axios.defaults.baseURL = baseUrl;
    } else {
      axios.defaults.baseURL = config.restUrl;
    }
    axios.defaults.headers.common.Authorization = "Bearer " + config.jwt;
    axios.defaults.headers.common["X-CSRF-Token"] = config.csrfToken;
    axios.defaults.headers.common.crossDomain = true;
    axios.interceptors.response.use(null, (error) => {
      if (axios.isCancel(error)) {
        console.log("Request canceled", error.message);
      } else {
        raftRestClient.logErrors(error);
        return Promise.reject(error);
      }
    });
  },
  logErrors(error) {
    // Only log for specified errors.
    const logfor = [403, 404, 422, 500];
    if (
      typeof error.response !== "undefined" &&
      typeof error.response.status !== "undefined" &&
      logfor.indexOf(error.response.status) === -1
    ) {
      return;
    }
    // Prevent duplicates/recursion
    if (typeof error.logSent !== "undefined" && error.logSent) {
      return;
    }
    error.logSent = true;

    // get response data if we can
    let response = "[non-JSON response]";
    if (
      typeof error.response !== "undefined" &&
      typeof error.response.headers !== "undefined" &&
      typeof error.response.headers["content-type"] !== "undefined" &&
      error.response.headers["content-type"] === "application/json"
    ) {
      response = error.response.data;
    } else if (typeof error.response === "undefined") {
      error.response = { status: 0, statusText: "No response" };
    }

    const axiosClient = axios.create({
      baseURL: config.baseUrl,
    });

    const json_message = {
      message: error.message,
      url: error.config.url,
      method: error.config.method,
      data: error.config.data,
      response: response,
      status: error.response.status,
      statusText: error.response.statusText,
      module: config.module,
    };
    axiosClient
      .post(
        "/raft/rest/log?_format=json",
        {
          message: JSON.stringify(json_message),
          channel: json_message.module,
        },
        {
          headers: { "Content-Type": "application/json" },
        },
      )
      .catch((error) => {
        console.error(error);
      });
  },
  getBase(path, queries, callback, error_callback, context, autoCancel = true) {
    this.initAxiosClient(config.baseUrl);
    return this._get(
      path,
      queries,
      callback,
      error_callback,
      context,
      autoCancel,
    );
  },
  getRest(path, queries, callback, error_callback, context, autoCancel = true) {
    this.initAxiosClient();
    return this._get(
      path,
      queries,
      callback,
      error_callback,
      context,
      autoCancel,
    );
  },
  _get(path, queries, callback, error_callback, context, autoCancel) {
    if (autoCancel && this.call[path] && typeof this.call[path] === "object") {
      this.call[path].cancel("Canceled overlapping call to " + path);
    }
    const ctoken = axios.CancelToken.source();
    if (this.call[path]) {
      // This allows cancelling multiple requests to the same path. An example
      // use case is needing to cancel multiple requests that have been made to
      // one path that differ by URL queries or post data.
      if (!this.call.hasOwnProperty(path + ".all")) {
        this.call[path + ".all"] = [];
        this.call[path + ".all"].push(this.call[path]);
      }
      this.call[path + ".all"].push(ctoken);
    }
    this.call[path] = ctoken;
    if (typeof queries !== "object" || !queries) {
      queries = {};
    }
    queries._format = "json";
    return axios
      .get(path, {
        cancelToken: this.call[path].token,
        params: queries,
      })
      .then((response) => {
        if (response) {
          return callback.call(context, response.data);
        }
      })
      .catch((error) => {
        if (typeof error_callback === "function") {
          error_callback.call(
            context,
            error.message,
            raftRestClient.getErrorResponseData(error),
          );
        } else {
          console.error(error);
        }
      });
  },
  headRest(path, queries, callback, errorCallback, context, baseUrl = "") {
    this.initAxiosClient(baseUrl);
    if (typeof queries !== "object" || !queries) {
      queries = {};
    }
    queries._format = "json";
    axios
      .head(path, {
        params: queries,
      })
      .then((response) => {
        if (response) {
          callback.call(context, response.data);
        }
      })
      .catch((error) => {
        if (typeof errorCallback === "function") {
          errorCallback.call(
            context,
            error.message,
            raftRestClient.getErrorResponseData(error),
          );
        } else {
          console.error(error);
        }
      });
  },
  patchRest(path, data, callback, error_callback, context) {
    this.initAxiosClient();
    axios
      .patch(path, data, {
        headers: this.headers,
      })
      .then((response) => {
        callback.call(context, response.data);
      })
      .catch((error) => {
        if (typeof error_callback === "function") {
          error_callback.call(
            context,
            error.message,
            raftRestClient.getErrorResponseData(error),
          );
        } else {
          console.error(error);
        }
      });
  },
  /**
   * Post data to a REST endpoint.
   *
   * @param {String} path
   * @param {Object} data
   * @param {Function} callback
   * @param {Function} error_callback
   * @param {Object} context
   */
  postRest(path, data, callback, errorCallback, context) {
    this.initAxiosClient();
    this._post(path, data, callback, errorCallback, context);
  },
  postBase(path, data, callback, errorCallback, context) {
    this.initAxiosClient(config.baseUrl);
    this._post(path, data, callback, errorCallback, context);
  },
  _post(path, data, callback, errorCallback, context) {
    axios
      .post(path, data, {
        headers: this.headers,
      })
      .then((response) => {
        callback.call(context, response.data, response.status);
      })
      .catch((error) => {
        if (typeof errorCallback === "function") {
          errorCallback.call(
            context,
            error.message,
            raftRestClient.getErrorResponseData(error),
          );
        } else {
          console.error(error);
        }
      });
  },
  getErrorResponseData(error) {
    let data = null;
    if (error.response && error.response.data) {
      data = error.response.data;
    }
    return data;
  },
  fileDownload(path, callback, error_callback, context) {
    this.initAxiosClient(config.baseUrl);
    axios
      .get(path, {
        responseType: "blob",
        headers: this.headers,
      })
      .then((response) => {
        callback.call(context, response.data);
      })
      .catch((error) => {
        if (typeof error_callback === "function") {
          error_callback.call(
            context,
            error.message,
            raftRestClient.getErrorResponseData(error),
          );
        } else {
          console.error(error);
        }
      });
  },
  csvStreamDownload(
    csvPath,
    queryString,
    fileName,
    filePrependDate = true,
    ctx = null,
    onStateChangeCallback = null,
  ) {
    const request = new XMLHttpRequest();
    const b4Url = config.baseUrl;
    request.open("POST", b4Url + csvPath + "?_format=csv");

    if (filePrependDate) {
      let date = new Date();
      date =
        date.getMonth() + 1 + "-" + date.getDate() + "-" + date.getFullYear();
      fileName = date + "-" + fileName;
    }

    request.setRequestHeader("Authorization", "Bearer " + config.jwt);
    request.setRequestHeader(
      "Content-Type",
      "application/x-www-form-urlencoded",
    );
    request.setRequestHeader("X-HTTP-Method-Override", "GET");

    request.send(queryString);
    if (onStateChangeCallback) {
      request.onreadystatechange = ctx.onStateChangeCallback.call();
    }
    request.onload = function (request, event) {
      const blob = new Blob([request.target.response], {
        type: "text/csv; charset=utf-8",
      });
      FileSaver.saveAs(blob, fileName);
    };
  },
};

export default raftRestClient;
